/**
 * Coach client list with status indicators. Reads GET /api/coach/clients,
 * which returns the signed-in coach's own clients, plus (for supertrainers)
 * clients with no coach assigned yet, flagged `unassigned`.
 *
 * Status: new (never logged a workout), active (trained in the last 7 days),
 * at_risk (8–14 days), paused (trained before, nothing for 14+ days).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';

type Status = 'new' | 'active' | 'paused' | 'at_risk';

interface Client {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url: string | null;
  last_workout_date: string | null;
  status: Status;
  unassigned?: boolean;
  last_seen_at?: string | null;
}

/** "Active today" / "Active 3 d ago" / "Not active yet" from last_seen_at (UTC). */
function lastActive(ts: string | null | undefined): string {
  if (!ts) return 'Not active yet';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 60) return 'Active now';
  if (d.toDateString() === new Date().toDateString()) return 'Active today';
  const days = Math.max(1, Math.round(mins / 1440));
  return days === 1 ? 'Active yesterday' : `Active ${days} d ago`;
}

const STATUS_COLOR: Record<Status, string> = {
  new: 'var(--info)',
  active: 'var(--success)',
  at_risk: 'var(--error)',
  paused: 'var(--warning)',
};

/** Name to show: display name, else first + last, else the email. Never blank. */
function nameOf(c: Client): string {
  return c.display_name?.trim() || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.email;
}

function initialsOf(c: Client): string {
  const n = nameOf(c);
  if (n.includes('@')) return n[0].toUpperCase();
  return n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

function ClientRow({ client }: { client: Client }) {
  return (
    <Link to={`/coach/clients/${client.id}`}>
      <div className="card flex items-center gap-3 cursor-pointer transition-all hover:border-[var(--gold-muted)]">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
          style={{ background: 'var(--gold)', color: 'var(--navy)' }}
        >
          {initialsOf(client)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm truncate">{nameOf(client)}</p>
          <p className="text-xs truncate" style={{ color: 'var(--gray-400)' }}>
            {lastActive(client.last_seen_at)} · {client.last_workout_date ? `Last workout ${client.last_workout_date}` : 'No workouts yet'}
          </p>
        </div>
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_COLOR[client.status] ?? 'var(--gray-500)' }} />
      </div>
    </Link>
  );
}

export default function CoachClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api
      .get<{ clients: Client[] }>('/api/coach/clients')
      .then((data) => setClients(data.clients || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const q = search.toLowerCase();
  const matches = (c: Client) => `${nameOf(c)} ${c.email}`.toLowerCase().includes(q);
  const mine = clients.filter((c) => !c.unassigned);
  const unassigned = clients.filter((c) => c.unassigned);

  // Stats describe the coach's own roster (unassigned sign-ups listed separately).
  const stats: { label: string; value: number; color: string }[] = [
    { label: 'New', value: mine.filter((c) => c.status === 'new').length, color: STATUS_COLOR.new },
    { label: 'Active', value: mine.filter((c) => c.status === 'active').length, color: STATUS_COLOR.active },
    { label: 'Paused', value: mine.filter((c) => c.status === 'paused').length, color: STATUS_COLOR.paused },
    { label: 'At Risk', value: mine.filter((c) => c.status === 'at_risk').length, color: STATUS_COLOR.at_risk },
  ];

  if (loading) {
    return (
      <div className="px-4 flex flex-col gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card animate-pulse h-16" style={{ background: 'var(--surface)' }} />
        ))}
      </div>
    );
  }

  const mineShown = mine.filter(matches);
  const unassignedShown = unassigned.filter(matches);

  return (
    <div className="px-4 flex flex-col gap-4">
      <PageHeader title="Clients" />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="card text-center py-2">
            <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-[10px]" style={{ color: 'var(--gray-400)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        className="input"
        placeholder="Search clients..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* My clients */}
      <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
        My clients · {mine.length}
      </p>
      {mineShown.length === 0 ? (
        <div className="card text-center py-8">
          <p className="text-white font-semibold">{mine.length ? 'No clients match' : 'No clients assigned to you yet'}</p>
        </div>
      ) : (
        mineShown.map((client) => <ClientRow key={client.id} client={client} />)
      )}

      {/* Unassigned (supertrainers only — the API only returns these to them) */}
      {unassigned.length > 0 && (
        <>
          <div className="mt-2">
            <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>
              Unassigned · {unassigned.length}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--gray-500)' }}>
              New sign-ups without a coach. Assign them in the admin app under Clients.
            </p>
          </div>
          {unassignedShown.map((client) => <ClientRow key={client.id} client={client} />)}
        </>
      )}
    </div>
  );
}
